import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    FormControlLabel,
    Checkbox,
    RadioGroup,
    Radio,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useTheme
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Table, TablePage } from "../types/interfaces";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import { useNavigate } from "react-router-dom";
import { createUUID } from "../utils/validators";

interface Props {
    table: Table;
    setTable: React.Dispatch<React.SetStateAction<Table>>;
    setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}

const PagesPanel: React.FC<Props> = ({ table, setTable, setIsDirty }) => {
    const theme = useTheme();
    const [pages, setPages] = useState<TablePage[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [newPageName, setNewPageName] = useState("");
    const [newPageType, setNewPageType] = useState<"Add" | "Edit" | "View">("View");
    const [isPopup, setIsPopup] = useState(false);
    const [helpAnchorEl, setHelpAnchorEl] = useState<HTMLElement | null>(null);
    const helpOpen = Boolean(helpAnchorEl);
    const handleHelpOpen = (event: React.MouseEvent<HTMLElement>) => setHelpAnchorEl(event.currentTarget);
    const handleHelpClose = () => setHelpAnchorEl(null);
    const [showPublished, setShowPublished] = useState(true);
    const [showDeleted, setShowDeleted] = useState(false);

    useEffect(() => {
        setPages(Array.isArray(table.pages) ? table.pages : []);
    }, [table.pages]);

    const filteredPages = pages.filter(p => {
        if (!showDeleted && p.deleted) return false;
        if (showPublished && !p.isPublished) return false;
        return true;
    });


    const handleAddPage = () => {
        const trimmedName = newPageName.trim();
        if (!trimmedName) {
            alert("Page name is required.");
            return;
        }

        if (pages.some(p => p.pageName.toLowerCase() === trimmedName.toLowerCase())) {
            alert("A page with this name already exists.");
            return;
        }

        const fullType = isPopup ? `Popup ${newPageType}` : newPageType;

        const newPage: TablePage = {
            pageID: createUUID(),
            tableID: table.tableID,
            pageType: fullType,
            pageName: trimmedName,
            layout: "",
            version: 1,
            isPublished: false,
            createdAt: new Date().toISOString(),
            deleted: false
        };

        const updatedPages = [...pages, newPage];
        setPages(updatedPages);
        setTable(prev => ({ ...prev, pages: updatedPages }));
        setIsDirty(true);

        setShowModal(false);
        setNewPageName("");
        setNewPageType("View");
        setIsPopup(false);
    };

    const handleEditPage = (page: TablePage) => {
        if (!page?.pageID) return;
        //navigate(`/pages/designer?pageid=${page.pageID}`);
        window.open(`/pages/designer?pageid=${page.pageID}`, "_blank");
    };


    const handleDeletePage = (pageID: string) => {
        if (!window.confirm("Are you sure you want to delete this page?")) return;

        const updatedPages = pages.filter(p => p.pageID !== pageID);
        setPages(updatedPages);
        setTable(prev => ({ ...prev, pages: updatedPages }));
        setIsDirty(true);
    };

    const columnDefs: ColDef<TablePage>[] = [
        { field: "pageName", headerName: "Name", flex: 2 },
        { field: "pageType", headerName: "Type", flex: 2 },
        {
            headerName: "Actions",
            flex: 2,
            cellRenderer: (params: ICellRendererParams<TablePage>) => {
                const page = params.data!;
                return (
                    <Box display="flex" gap={1}>
                        <Button
                            size="small"
                            variant="contained"
                            className="action-button"
                            onClick={() => handleEditPage(page)}
                        >
                            Edit
                        </Button>

                        {!page.isPublished && (
                            <Button
                                size="small"
                                variant="contained"
                                color="error"
                                className="action-button"
                                onClick={() => handleDeletePage(page.pageID)}
                            >
                                Delete
                            </Button>
                        )}
                    </Box>
                );
            }
        }
    ];

    const navigate = useNavigate();


    return (
        <Box
            sx={{
                padding: 3,
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                borderRadius: 2,
                width: '100%',
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Custom Pages</Typography>
                <Button variant="contained" onClick={() => setShowModal(true)}>
                    + Add Page
                </Button>
            </Box>

            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Typography variant="subtitle1">Page Types</Typography>
                <IconButton size="small" onClick={handleHelpOpen}>
                    <HelpOutlineIcon fontSize="small" />
                </IconButton>
            </Box>

            <Popover
                open={helpOpen}
                anchorEl={helpAnchorEl}
                onClose={handleHelpClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box p={2} maxWidth={300}>
                    <Typography variant="subtitle2" gutterBottom>Page Types:</Typography>
                    <ul style={{ paddingLeft: "1rem", margin: 0 }}>
                        <li><strong>Add</strong>: Form to create a new record</li>
                        <li><strong>Edit</strong>: Form to update a record</li>
                        <li><strong>View</strong>: Read-only display</li>
                        <li><strong>Popup Add</strong>: Add via modal</li>
                        <li><strong>Popup Edit</strong>: Edit in a popup</li>
                        <li><strong>Popup View</strong>: Read-only popup view</li>
                    </ul>
                </Box>
            </Popover>


            <Box className={`ag-theme-${theme.palette.mode === 'dark' ? 'alpine-dark' : 'alpine'}`} style={{ height: 400, width: '100%' }}>
                <Box display="flex" gap={2} alignItems="center" mb={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showPublished}
                                onChange={(e) => setShowPublished(e.target.checked)}
                            />
                        }
                        label="Published"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showDeleted}
                                onChange={(e) => setShowDeleted(e.target.checked)}
                            />
                        }
                        label="Show Deleted"
                    />
                </Box>

                <AgGridReact
                    columnDefs={columnDefs}
                    rowData={filteredPages}
                    domLayout="autoHeight"
                    headerHeight={32}
                    rowHeight={48}
                    defaultColDef={{ filter: true, sortable: true, flex: 1 }}
                    onRowDoubleClicked={(event) => {
                        if (event.data) handleEditPage(event.data);
                    }}
                />
            </Box>

            {/* Add Page Dialog */}
            <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add New Page</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Page Name"
                        fullWidth
                        margin="dense"
                        value={newPageName}
                        onChange={(e) => setNewPageName(e.target.value)}
                    />

                    <Typography variant="subtitle2" mt={2}>Page Type</Typography>
                    <RadioGroup
                        row
                        value={newPageType}
                        onChange={(e) => setNewPageType(e.target.value as "Add" | "Edit" | "View")}
                    >
                        <FormControlLabel value="Add" control={<Radio />} label="Add" />
                        <FormControlLabel value="Edit" control={<Radio />} label="Edit" />
                        <FormControlLabel value="View" control={<Radio />} label="View" />
                    </RadioGroup>

                    <FormControlLabel
                        control={<Checkbox checked={isPopup} onChange={() => setIsPopup(!isPopup)} />}
                        label="Use as Popup"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowModal(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddPage}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PagesPanel;
